import type { SmartSort } from '@iheartjane/dm-sdk';
import { Button, Flex, Typography } from '@iheartjane/reefer';
import { pluralize } from '@iheartjane/utils';
import kebabCase from 'lodash-es/kebabCase';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import type { AlgoliaProduct } from '@jane/search/types';
import type { Store } from '@jane/shared/types';

import { useGetCart } from '../../data-access';
import { useKioskSpacing } from '../../hooks';
import { Grid } from './Grid/Grid';
import { MenuProductCard } from './ProductCard/MenuProductCard';
import { productCardMinWidth } from './ProductCard/ProductCard';

interface CustomRowDisplayProps {
  columnCount: number;
  hideViewAll?: boolean;
  productCount?: number;
  rowId?: number;
  rowInstance: SmartSort<AlgoliaProduct> | undefined;
  store: Store;
  subtitle?: React.ReactNode;
  title: string;
}

export const CustomRowDisplay = ({
  columnCount,
  rowInstance,
  hideViewAll,
  productCount,
  rowId,
  store,
  subtitle,
  title,
}: CustomRowDisplayProps) => {
  const navigate = useNavigate();
  const { data: cart } = useGetCart();
  const { isIpad } = useKioskSpacing();

  const onViewAll = useCallback(() => {
    if (rowId) {
      navigate(`/stores/${store.id}/menu/rows/${rowId}/${kebabCase(title)}`);
    } else {
      navigate(
        `/stores/${store.id}/menu/featured/${encodeURIComponent(
          kebabCase(title)
        )}`
      );
    }
  }, [navigate, store.id, title, rowId]);

  const { defaultSpacing } = useKioskSpacing({
    defaultPadSize: 48,
    defaultSize: 64,
  });

  if (!productCount || !rowInstance) return <div></div>;

  const customRowProducts = rowInstance.products;

  return (
    <Flex
      ariaLabel={`${title} custom row`}
      flexDirection="column"
      px={defaultSpacing}
      width="100%"
    >
      <Flex
        alignItems="center"
        flexDirection="row"
        justifyContent="space-between"
        mb={24}
        width="100%"
      >
        <Flex flexDirection="column">
          <Typography variant="section-bold">{title}</Typography>
          {subtitle}
        </Flex>
        {!hideViewAll && productCount > 0 && (
          <Typography>
            {pluralize({
              noun: 'Product',
              number: productCount,
            })}
          </Typography>
        )}
      </Flex>
      {customRowProducts && (
        <>
          <Grid
            gap={24}
            gridTemplateColumns={`repeat(auto-fill, minmax(${productCardMinWidth}px, 1fr))`}
            style={{ position: 'relative', width: '100%' }}
          >
            {customRowProducts.slice(0, columnCount).map((product) => (
              <MenuProductCard
                key={product.objectId}
                product={product}
                store={store}
                cart={cart}
              />
            ))}
          </Grid>
          {!hideViewAll && productCount > columnCount && (
            <Button
              ariaLabel={`View all products for ${title}`}
              full
              size={isIpad ? 'small' : 'default'}
              label={
                <Typography variant={isIpad ? 'body-bold' : 'section-bold'}>
                  View all products
                </Typography>
              }
              mt={24}
              onClick={onViewAll}
              variant="tertiary"
            />
          )}
        </>
      )}
    </Flex>
  );
};
